import type { BadgeTone } from '@zarya/ui';
import { t } from '@app/i18n';
import type { components } from '@app/api/types';
import { operatorText } from '@app/main/Policies/PolicyModel';

/**
 * Vocabulário da **causa** de uma decisão — T16 (chip), T17 (bloco «Por que
 * negou») e T18 (o mesmo chip e o mesmo bloco, simulados).
 *
 * ## Uma fonte só, e ela não é esta
 *
 * Quem **classifica** a causa é o PDP: o Rego publica `result.cause` ao lado do
 * `result.diagnostico`, os coletores (mock-aig e middleware real) só **copiam**,
 * e o produto só **exibe**. Nada aqui deduz causa de `deny_reason`, de status
 * HTTP ou do veredito — um produto que adivinha passa a ensinar uma coisa
 * enquanto o PDP decide outra, e a tela fica convincente e errada.
 *
 * `cause` e `deny_reason` podem até **discordar na ordem** (a do `deny_reason` é
 * histórica, catálogo antes de barreira). `cause` é a verdade classificada: o
 * chip sai dela, e o `deny_reason`/`detail` fica sendo só a frase.
 *
 * O que este módulo faz é tradução (`CAUSA`: enum → pt-BR) e **leitura** do
 * diagnóstico (`whatWouldPass`, `orderedRules`: o que já está escrito lá, dito
 * em português). Contrato: `docs/avaliacao/decisoes-causa-contrato.md` (v4 —
 * todas as chaves EN; as duas causas de fora do padrão desde 07/10).
 */

export type Causa = components['schemas']['DecisionCause'];
export type Diagnostic = components['schemas']['DecisionDiagnostic'];
export type Barriers = NonNullable<components['schemas']['DecisionBarriers']>;
export type RuleEvaluated = components['schemas']['DecisionRuleEvaluated'];

/** As causas na ordem em que o PDP avalia — a ordem do filtro e da documentação. */
export const CAUSAS: Causa[] = [
  'allowed',
  // aim-uig.40 (v4): a chamada de um agente a uma API fora de api/, mcp/ e a2a/. Logo depois de
  // `allowed`, na ordem da cadeia do Rego: o PDP decide só pela opção do gateway, antes de tudo.
  'ungoverned_allowed',
  'ungoverned_denied',
  'sts_trust_not_published',
  'identity_not_attested',
  'token_invalid',
  'token_not_bound',
  'token_not_action',
  'manual_module_denied',
  'a2a_not_granted',
  'rpc_method_not_allowed',
  'workload_axis',
  'person_axis',
  'limit_exceeded',
  'data_scope',
  'unknown',
];

export interface CausaVocab {
  /** O texto do chip. Três causas de token compartilham «token», como o contrato pede. */
  rotulo: string;
  tone: BadgeTone;
  /** Desambigua as três de token no filtro, onde o rótulo sozinho se repetiria. */
  filtro: string;
  /** O que a causa afirma, para o tooltip do chip. */
  tip: string;
  /**
   * `true` = o enum a declara e **o PDP não a emite hoje** (contrato v3).
   *
   * Fica no catálogo de propósito: um executor que comece a emiti-la amanhã não
   * precisa de mudança de contrato, e um filtro por ela devolve lista vazia em
   * vez de 400. O que ela NÃO ganha é caso de teste esperando vê-la sair do
   * mock — um teste desses passaria a mentir sobre o que o PDP faz.
   */
  reservada?: true;
}

/**
 * Catálogo pt-BR (contrato §3). Identificadores em EN, texto em português — e o
 * texto vive **num lugar só**, porque T16, T17 e T18 falam da mesma coisa e uma
 * segunda cópia divergiria na primeira revisão de vocabulário.
 */
const CAUSA_TOM: Record<Causa, { tone: BadgeTone; reservada?: true }> = {
  allowed: { tone: 'success' },
  // C16: passou, mas fora da governança — `warning`, e não `success`.
  ungoverned_allowed: { tone: 'warning' },
  ungoverned_denied: { tone: 'danger' },
  sts_trust_not_published: { tone: 'danger' },
  identity_not_attested: { tone: 'danger' },
  token_invalid: { tone: 'danger' },
  token_not_bound: { tone: 'danger' },
  token_not_action: { tone: 'danger' },
  manual_module_denied: { tone: 'warning' },
  a2a_not_granted: { tone: 'danger' },
  rpc_method_not_allowed: { tone: 'danger' },
  workload_axis: { tone: 'danger' },
  person_axis: { tone: 'danger' },
  limit_exceeded: { tone: 'warning', reservada: true },
  data_scope: { tone: 'warning', reservada: true },
  unknown: { tone: 'neutral' },
};

/**
 * Catálogo pt-BR (contrato §3), agora vindo do `decisions.*`. Identificadores em
 * EN, texto no locale — e o texto vive **num lugar só**, porque T16, T17 e T18
 * falam da mesma coisa e uma segunda cópia divergiria na primeira revisão de
 * vocabulário.
 *
 * As três causas de token compartilham o rótulo «token», como o contrato pede,
 * e se desambiguam pelo `.filter` — que é o que o filtro mostra.
 */
export function causaVocab(valor: Causa): CausaVocab {
  return {
    rotulo: t(`decisions.cause.${valor}`),
    filtro: t(`decisions.cause.${valor}.filter`),
    tip: t(`decisions.cause.${valor}.tip`),
    ...CAUSA_TOM[valor],
  };
}

/**
 * Fora do padrão (aim-uig.40 §6): o PDP decidiu só pela opção «Recursos fora do padrão»
 * do gateway. Não há barreira nem eixo nessas decisões — por desenho, e não por um
 * executor antigo —, e quem as mostra não desenha nenhum dos dois.
 */
export function isUngoverned(causa: Causa): boolean {
  return causa === 'ungoverned_allowed' || causa === 'ungoverned_denied';
}

/**
 * A causa de um item/detalhe, com o padrão do contrato.
 *
 * Ausente vira `unknown` — que é exatamente o que o coletor grava — e **não**
 * se deriva do veredito: um DENY sem causa é um DENY que ninguém explicou, e
 * dizer «faltou no humano» ali seria inventar o diagnóstico que falta.
 */
export function causaOf(causa: Causa | null | undefined): Causa {
  return causa && (CAUSAS as string[]).includes(causa) ? causa : 'unknown';
}

/**
 * A explicação de uma delegação A2A **sem diagnóstico** (DEF-34, 04/09; escopo
 * reduzido pelo DEF-29 em 09/09).
 *
 * Até 08/09 uma decisão A2A nunca trazia diagnóstico, por desenho: o Rego a
 * decidia por `agentes_permitidos` (o `a2a.needs` do contrato do chamador), antes
 * e fora dos eixos. Ler a ausência do diagnóstico como «executor anterior a esta
 * versão» — o que o bloco fazia — era acusar de velho um executor que registrou a
 * decisão certa.
 *
 * Desde o DEF-29 a delegação é capability e **traz** os dois eixos; quando eles
 * vêm, quem desenha é o bloco por eixo, o mesmo das tools. Estas frases ficam
 * para as decisões gravadas antes disso, que continuam no log e continuam sendo
 * abertas.
 *
 * As frases não deduzem nada: o veredito e a causa vêm do PDP; o que se escreve
 * aqui é o **modelo** (quem decide o quê) e o que a causa já diz.
 */
export interface A2AExplanation {
  /** A frase fixa do modelo: quem decidiu, e sem o quê. */
  modelo: string;
  /** O veredito lido, em uma frase — ou `null` quando não há veredito nem causa que o diga. */
  veredito: string | null;
}

// A frase NÃO usa as palavras «sem diagnóstico», e isso é deliberado: «sem diagnóstico» é o
// rótulo do chip de causa `unknown` — «o PDP não disse» —, e foi exatamente essa leitura que o
// DEF-34 tirou da delegação. Dizer «registrada antes dos eixos» conta o mesmo fato (não há
// diagnóstico a mostrar) sem devolver a decisão ao vocabulário do «não sei».
export function a2aModelPhrase(): string {
  return t('decisions.a2a.modelPhrase');
}

export function a2aExplanation(opcoes: {
  verdict?: string | null;
  causa: Causa | null | undefined;
  /** Quem chamou (o generalista) e quem foi chamado (o especialista). */
  caller?: string | null;
  agent?: string | null;
}): A2AExplanation {
  const { verdict, causa, caller, agent } = opcoes;
  const resolved = causaOf(causa);
  const who = agent ? t('decisions.a2a.agent', { agent }) : t('decisions.a2a.agentFallback');
  const from = caller ? t('decisions.a2a.caller', { caller }) : t('decisions.a2a.callerFallback');

  let veredito: string | null = null;
  if (resolved === 'allowed' || verdict === 'permitido') {
    veredito = t('decisions.a2a.allowed', { who, from });
  } else if (resolved === 'a2a_not_granted') {
    veredito = t('decisions.a2a.denied', { who, from });
  } else if (verdict === 'negado') {
    // Negada por outra causa (uma barreira antes da A2A, um módulo manual): a
    // causa nomeia; a frase não adivinha.
    veredito = t('decisions.a2a.deniedBefore', { cause: causaVocab(resolved).rotulo });
  } else if (verdict === 'indeterminado') {
    veredito = t('decisions.a2a.undetermined');
  }
  return { modelo: a2aModelPhrase(), veredito };
}

/** As quatro barreiras: do atalho do detalhe, ou de dentro do diagnóstico. */
export function barriersOf(
  barreiras: components['schemas']['DecisionBarriers'] | undefined,
  diagnostico: Diagnostic | null | undefined,
): Barriers | null {
  const lidas = barreiras ?? diagnostico?.barriers ?? null;
  // «Identidade atestada» aparece como REAL (aim-ivi.2, decisão 3 do mrolim, 11/10/2026). Desde a D4 a simulação
  // descreve a identidade pelo SPIFFE ID, e o executor manda essa barreira como `"simulated"` em toda simulação. A
  // tela não mostra o selo «simulada» nela, nem uma dica nova: nenhum texto pode fazer quem olha duvidar do que vê.
  // Quando o certificado real chegar à simulação, o valor vem do PDP e esta linha sai. O contrato do console ainda
  // tipa a barreira como `boolean | null`, por isso a comparação passa por `unknown`. O `"simulated"` do token, no
  // modo Claims, fica como era.
  if ((lidas?.identity_attested as unknown) === 'simulated') return { ...lidas, identity_attested: true };
  return lidas;
}

/** Rótulo e frase de cada barreira, na ordem em que o PDP as avalia. */
export const BARRIER_KEYS: (keyof Barriers)[] = [
  'identity_attested',
  'token_valid',
  'token_bound',
  'action_token',
];

/** Rótulo e frase de cada barreira, na ordem em que o PDP as avalia. */
export function barrier(chave: keyof Barriers): { rotulo: string; comoResolver: string } {
  return {
    rotulo: t(`decisions.barrier.${chave}`),
    comoResolver: t(`decisions.barrier.${chave}.howTo`),
  };
}

/**
 * O que `token_valid: "simulated"` afirma — e o que ele NÃO afirma.
 *
 * Afirma que a assinatura não foi conferida: o modo simulação manda um JWT
 * `alg: none` e o PDP lê as claims como dadas, porque a rota de simulação pede
 * isso explicitamente. Desde a D4 do aim-ivi o vínculo com o workload não vem de
 * um certificado: a simulação descreve a identidade pelo SPIFFE ID, e a tela mostra
 * a barreira da identidade como real (`barriersOf`, decisão 3 do aim-ivi.2).
 *
 * **Não** afirma que a decisão é ensaio. Uma simulação com JWT real passa pela
 * troca no STS e volta `true`. Quem responde «isto foi ensaio?» é
 * `Decision.simulated`, e é por ele que T16 corta.
 */
export function simulatedBarrierTip(): string {
  return t('decisions.barrier.simulated.tip');
}

/** Qual barreira cada causa de barreira nomeia. As demais causas não são barreira. */
const BARRIER_BY_CAUSE: Partial<Record<Causa, keyof Barriers>> = {
  identity_not_attested: 'identity_attested',
  token_invalid: 'token_valid',
  token_not_bound: 'token_bound',
  token_not_action: 'action_token',
};

export interface CausaContext {
  /** A tool pedida. */
  tool?: string | null;
  /**
   * O agente ALVO, quando a chamada é uma delegação (DEF-29, 09/09).
   *
   * Presente, ele **substitui** a tool como sujeito das frases: uma delegação
   * não tem tool, e «adicionar a tool pedida ao contrato» é conselho para um
   * lugar onde ela não mora.
   */
  agent?: string | null;
  /** Como a tela chama a identidade que chamou (slug, ou o SPIFFE ID). */
  identity?: string | null;
  /** As claims do token, como texto («bruna · analista»). */
  claims?: string | null;
}

/** `expected`/`received` vêm sem tipo do PDP: texto, número ou lista. */
export function valueText(valor: unknown): string {
  if (valor === null || valor === undefined) return t('decisions.value.absent');
  if (Array.isArray(valor)) return valor.map((v) => valueText(v)).join(', ');
  if (typeof valor === 'object') return JSON.stringify(valor);
  return String(valor);
}

const OF = (valor: string | null | undefined, fallback: string) =>
  valor && valor.trim() ? valor.trim() : fallback;

/** Quantas regras a tela mostra antes de resumir o resto em «+n». */
export const RULES_SHOWN = 3;

// --- as duas chaves que a delegação usa no lugar das da tool (DEF-29, 09/09) ------
//
// Uma decisão A2A traz o MESMO diagnóstico de uma tool, com dois nomes próprios:
// `contract_agents[]` no lugar de `contract_tools[]` e `grants_agent` no lugar de
// `grants_tool`. Os nomes são honestos — a lista é de delegações, não de tools —,
// e é por isso que a leitura é por par e não por renomeação.
//
// As duas chaves **não estão no `DecisionDiagnostic` do OpenAPI** (o contrato
// declara só as da tool), então a leitura é indexada e defensiva. Registrado ao
// lead em `docs/progress/def29-delegacao-front.md`: é campo emitido e não
// declarado, e o lugar de consertar é o contrato, não esta função.

/**
 * O que a regra afirma — `grants_tool` numa tool, `grants_agent` numa delegação.
 *
 * Nunca as duas na mesma regra: o mock publica uma OU outra conforme a espécie
 * avaliada. `undefined` continua sendo «o diagnóstico não disse», que é diferente
 * de `false`.
 */
export function ruleGrants(rule: RuleEvaluated): boolean | null | undefined {
  if (rule.grants_tool !== undefined) return rule.grants_tool;
  const granted = (rule as { grants_agent?: unknown }).grants_agent;
  return typeof granted === 'boolean' ? granted : undefined;
}

/** O que o contrato concede — tools, ou as delegações de uma decisão A2A. */
export function contractGrants(
  axis: NonNullable<Diagnostic>['workload_axis'] | null | undefined,
): string[] | undefined {
  if (!axis) return undefined;
  if (axis.contract_tools !== undefined) return axis.contract_tools;
  const agents = (axis as { contract_agents?: unknown }).contract_agents;
  return Array.isArray(agents)
    ? agents.filter((item): item is string => typeof item === 'string')
    : undefined;
}

/**
 * As regras avaliadas em ordem de **proximidade** — a que quase passou primeiro.
 *
 * ## Por que ordenar, e não só listar
 *
 * `evaluated_rules[]` é toda regra ativa que cita a tool **ou** cobre a
 * identidade. Num tenant grande isso são dezenas, e a ordem em que o PDP as
 * avaliou não tem nada a ver com a que interessa a quem levou o 403. Sem ordem,
 * a regra que falhou por uma claim — a única acionável — aparece na posição 27.
 *
 * Os quatro grupos, do mais perto do «passaria» ao mais longe:
 *
 * 1. concede a tool, cobre a identidade e **uma condição falhou** — é o caso em
 *    que existe um valor concreto que faria passar;
 * 2. concede a tool e não cobre a identidade — falta alcance, não claim;
 * 3. cobre a identidade e não concede a tool — falta a tool na regra;
 * 4. o resto (inclusive o que o diagnóstico não classificou).
 *
 * Dentro de cada grupo, ordem alfabética do `name`: estável entre renders e
 * entre execuções, que é o que deixa duas leituras da mesma decisão
 * comparáveis.
 */
export function orderedRules(rules: RuleEvaluated[]): RuleEvaluated[] {
  const grupo = (r: RuleEvaluated): number => {
    // `ruleGrants`, e não `grants_tool`: numa delegação a mesma afirmação chega em
    // `grants_agent`, e ler só a chave da tool poria TODA regra de uma decisão
    // A2A no grupo 4 — a que quase passou desapareceria no fim da lista.
    const grants = ruleGrants(r);
    if (grants === true && r.covers_identity === true && r.failed_condition) return 1;
    if (grants === true && r.covers_identity !== true) return 2;
    if (r.covers_identity === true && grants !== true) return 3;
    return 4;
  };
  return [...rules].sort(
    (a, b) => grupo(a) - grupo(b) || (a.name ?? '').localeCompare(b.name ?? ''),
  );
}

/** A regra do grupo 1 — a única de que a sugestão pode falar. `null` se não há. */
export function closestRule(rules: RuleEvaluated[]): RuleEvaluated | null {
  const primeira = orderedRules(rules)[0];
  if (!primeira) return null;
  const grupo1 =
    ruleGrants(primeira) === true && primeira.covers_identity === true && primeira.failed_condition;
  return grupo1 ? primeira : null;
}

/**
 * **«O que faria passar»** — as frases derivadas do diagnóstico (contrato §3).
 *
 * Deriva, não decide: cada frase só reescreve em português o que o PDP já
 * escreveu no `diagnostico`. Quando o diagnóstico não diz (evento sem causa,
 * `unknown`), a lista sai **vazia** — e a tela mostra o vazio, em vez de um
 * palpite que pareceria conselho.
 */
export function whatWouldPass(
  causa: Causa,
  diagnostico: Diagnostic | null | undefined,
  ctx: CausaContext = {},
): string[] {
  // O SUJEITO da frase é a delegação quando há agente alvo, e a tool no resto.
  // Desde o DEF-29 uma delegação tem os dois eixos como qualquer capability, e
  // cai nas mesmas causas — o que muda é só o nome do que se pede.
  const delegation = ctx.agent && ctx.agent.trim() ? ctx.agent.trim() : null;
  const tool = delegation
    ? t('decisions.context.delegationTo', { agent: delegation })
    : OF(ctx.tool, t('decisions.context.toolFallback'));
  const identity = OF(ctx.identity, t('decisions.context.identityFallback'));

  // aim-sbu (03/10): a confiança no STS não publicada nega TUDO, antes das
  // barreiras. O remédio não é na identidade nem na pessoa: é no executor.
  if (causa === 'sts_trust_not_published') {
    return [t('decisions.wouldPass.sts_trust_not_published')];
  }

  // Barreira: **texto fixo por barreira** (contrato §3), escolhido pela causa e
  // não pelos bools. A causa é o que o PDP afirmou; os bools são o retrato do
  // diagnóstico, que pode nem ter vindo — e num evento sem `diagnostico` a frase
  // continua valendo, porque a causa basta para saber qual barreira barrou.
  const doBarreira = BARRIER_BY_CAUSE[causa];
  if (doBarreira) {
    return [barrier(doBarreira).comoResolver];
  }

  if (causa === 'workload_axis') {
    if (delegation) {
      return [t('decisions.wouldPass.workload_axis.delegation', { agent: delegation, identity })];
    }
    return [t('decisions.wouldPass.workload_axis', { tool, identity })];
  }

  if (causa === 'person_axis') {
    const avaliadas = diagnostico?.person_axis?.evaluated_rules ?? [];

    // UMA frase, e ela vem da regra mais próxima de passar (grupo 1). Listar
    // todas as condições barradas seria devolver o diagnóstico em prosa: numa
    // negação com dez regras, dez «faria passar» não dizem o que fazer.
    const perto = closestRule(avaliadas);
    if (perto) {
      const c = perto.failed_condition!;
      return [
        t('decisions.wouldPass.person_axis.rule', {
          rule: OF(perto.name, t('decisions.context.ruleFallback')),
          claim: OF(c.claim, t('decisions.context.claimFallback')),
          // A etiqueta do operador, pela chave curta de Human Access (aim-zqo); o enum do contrato é português.
          operator: operatorText(c.operator, '='),
          expected: valueText(c.expected),
          received: valueText(c.received),
        }),
      ];
    }

    // Nenhuma regra chegou às condições. A frase afirma o FATO (nenhuma regra
    // afirma) e lista as claims **recebidas** — nunca uma claim inventada: só
    // uma regra pode dizer que claim ela exige, e aqui não há regra nenhuma
    // dizendo isso.
    const claims = OF(ctx.claims, t('decisions.context.claimsFallback'));
    return [t('decisions.wouldPass.person_axis.noRule', { tool, identity, claims })];
  }

  if (causa === 'a2a_not_granted') {
    // Com o alvo em mão, a frase o nomeia e diz a aba: é a especialização A2A do
    // eixo workload, e o remédio é o mesmo — conceder a delegação no contrato.
    if (delegation) {
      return [t('decisions.wouldPass.workload_axis.delegation', { agent: delegation, identity })];
    }
    return [t('decisions.wouldPass.a2a_not_granted', { identity })];
  }

  if (causa === 'rpc_method_not_allowed') {
    return [t('decisions.wouldPass.rpc_method_not_allowed')];
  }

  if (causa === 'manual_module_denied') {
    return [t('decisions.wouldPass.manual_module_denied')];
  }

  // `allowed`, `unknown` e as reservadas (`limit_exceeded`, `data_scope`): ou
  // não há o que passar, ou o diagnóstico não carrega o dado que diria como.
  // Silêncio é honesto.
  return [];
}
